import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useBusiness, Business } from '@/hooks/useBusiness';
import { useCategories, ReviewCategory } from '@/hooks/useCategories';
import { useReviews, GeneratedReview } from '@/hooks/useReviews';

interface AppContextType {
  // Auth
  user: User | null;
  session: Session | null;
  authLoading: boolean;
  signOut: () => Promise<void>;
  
  // Business
  business: Business | null;
  businessLoading: boolean;
  createBusiness: (data: Omit<Business, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<{ data?: Business; error?: Error }>;
  updateBusiness: (updates: Partial<Business>) => Promise<{ data?: Business; error?: Error }>;
  
  // Categories
  categories: ReviewCategory[];
  categoriesLoading: boolean;
  createCategory: (name: string, description?: string, keywords?: string[], usp?: string, location?: string) => Promise<{ data?: ReviewCategory; error?: Error }>;
  updateCategory: (id: string, updates: { keywords?: string[]; usp?: string; location?: string }) => Promise<{ data?: ReviewCategory; error?: Error }>;
  deleteCategory: (id: string) => Promise<{ success?: boolean; error?: Error }>;
  
  // Reviews
  reviews: GeneratedReview[];
  reviewsLoading: boolean;
  saveReviews: (reviews: Omit<GeneratedReview, 'id' | 'created_at' | 'copied' | 'submitted'>[]) => Promise<{ data?: GeneratedReview[]; error?: Error }>;
  markAsCopied: (id: string) => Promise<void>;
  markAsSubmitted: (id: string) => Promise<void>;
  
  // Stats
  stats: {
    reviewsGenerated: number;
    reviewsCopied: number;
    reviewsSubmitted: number;
    categories: number;
    keywords: number;
  };
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Set up auth state listener
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setAuthLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setAuthLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    // Clear local state first so UI immediately reflects logout
    setUser(null);
    setSession(null);
    setAuthLoading(false);

    try {
      // Best-effort server logout (may fail if session already expired)
      await supabase.auth.signOut();
    } catch (error) {
      // Ignore "session not found"-style errors; user is effectively logged out locally
      console.warn('Logout error (session may have been expired):', error);
    }
  };

  // Business hook
  const { 
    business, 
    loading: businessLoading, 
    createBusiness, 
    updateBusiness 
  } = useBusiness(user?.id);

  // Categories hook
  const { 
    categories, 
    loading: categoriesLoading, 
    createCategory, 
    updateCategory,
    deleteCategory 
  } = useCategories(business?.id);

  // Reviews hook
  const { 
    reviews, 
    loading: reviewsLoading, 
    saveReviews, 
    markAsCopied, 
    markAsSubmitted,
    stats: reviewStats 
  } = useReviews(business?.id);

  // Combined stats
  const stats = {
    reviewsGenerated: reviewStats.reviewsGenerated,
    reviewsCopied: reviewStats.reviewsCopied,
    reviewsSubmitted: reviewStats.reviewsSubmitted,
    categories: categories.length,
    keywords: reviews.reduce((acc, r) => acc + (r.keywords?.length || 0), 0),
  };

  return (
    <AppContext.Provider
      value={{
        user,
        session,
        authLoading,
        signOut,
        business,
        businessLoading,
        createBusiness,
        updateBusiness,
        categories,
        categoriesLoading,
        createCategory,
        updateCategory,
        deleteCategory,
        reviews,
        reviewsLoading,
        saveReviews,
        markAsCopied,
        markAsSubmitted,
        stats,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}
